import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiUrl } from './api';
import { getPasskeyAssertion } from './webauthn';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE SESSION. The access token lives in MEMORY and nowhere else.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **NOT `localStorage`, not `sessionStorage`, not a readable cookie**, and this is the same
 * decision WP8i-1 made on the server: *"a browser-held refresh token makes per-session revocation
 * unenforceable by construction."* The refresh token is already httpOnly and never reaches script.
 * Keeping the *access* token in a JS-readable store would undo half of that — anything that can run
 * in the page could lift it, and it authenticates every request for its whole lifetime.
 *
 * The cost is real and accepted: **a page reload loses the token.** What survives is the httpOnly
 * refresh cookie, so `restore()` below asks the API to mint a new access token from it on load. That
 * is one request per reload, and it is the request that makes the trade work.
 *
 * ⚠️ **`GET /auth/me` is what turns a token into a workspace.** Every other guarded route requires
 * `x-workspace-id`, and `decisions.md` R-3 refuses to guess "their only workspace" — so a
 * freshly-signed-in browser genuinely does not know one until it asks.
 */

export interface Workspace {
  readonly id: string;
  readonly slug: string;
  readonly memberId: string;
  /**
   * The **tenant's** lifecycle state, not the workspace's (`decisions.md` S-9, migration 0033).
   *
   * ⚠️⚠️ **THE ONLY WAY THIS APP CAN LEARN THAT ITS TENANT IS SUSPENDED.** Every
   * workspace-scoped request is refused by the guard with a 403 `tenant_suspended`; `/auth/me` is
   * account-level and deliberately keeps answering. Without this field the app would know only
   * that everything fails, which is indistinguishable from the API being down.
   *
   * ⚠️ **Optional over the wire, because an older API does not send it** — and a missing value
   * must mean *"not suspended"* rather than crashing the restore. `trial` and `active` are the
   * same thing to every screen.
   */
  readonly tenantStatus: 'trial' | 'active' | 'suspended';

  /**
   * C42 · ⚠️ **The name, which this type went without for twenty chunks.** `app.tsx` showed the
   * slug and said so in a comment: *"a workspace has no name on the wire, so inventing one here
   * would be inventing data — it gains a name when the workspace read does."* `0062` made that
   * read.
   *
   * ⚠️ Optional over the wire for `tenantStatus`'s reason — an older API does not send it — and it
   * falls back to the slug, which is what the shell drew before.
   */
  readonly name: string;

  /** C42 · whether `/files/workspace-icon/:id` has anything to serve. */
  readonly hasIcon: boolean;
}

export interface SessionState {
  readonly status: 'restoring' | 'signed-out' | 'signed-in';
  readonly email: string | null;
  readonly workspaces: readonly Workspace[];
  readonly workspaceId: string | null;
  /** C22 · the signed-in person's own name and picture, for the chrome. Either may be null. */
  readonly displayName: string | null;
  /**
   * ⚠️⚠️ **WHAT THEY ARE CALLED, WHICH IS NOT THE FIRST WORD OF `displayName`.** *"Takács
   * Gábor"* is family-name-first; the composed name cannot be split back into halves, so `0148`
   * sends this one separately. Anything addressing a person directly uses it.
   *
   * ⚠️ `null` on an account that has never filled the two fields in — a caller then says
   * something that does not need a name rather than guessing one.
   */
  readonly givenName: string | null;
  readonly avatarUrl: string | null;
  /**
   * ⚠️ `0202` · the first-run setup flow. `Gate` reads it: no workspace is step 2, a workspace and
   * `finished: false` is step 3. `organisationName` prefills step 2 for an invited admin.
   *
   * ⚠️ **`finished: true` when the API does not say**, so an older API — or a test stub — never
   * traps somebody in a flow they already finished.
   */
  readonly setup: {
    readonly finished: boolean;
    readonly organisationName: string | null;
    /** `0203` · `false` only for flow A's account before setup step 1 has named it. */
    readonly profileDone: boolean;
  };
  /**
   * `0203` · flow A: sign in with the six-digit code the sign-up email carried. The server answers
   * exactly as a password sign-in does, so the session is adopted the same way.
   */
  readonly signInWithCode: (email: string, code: string) => Promise<{ ok: boolean; code?: string }>;
  /**
   * @param remember The sign-in screen's *"Maradjak bejelentkezve ezen a gépen"*. C18.
   *
   * ⚠️ **Optional, defaulting to the previous behaviour.** It governs the refresh cookie's
   * `Max-Age` on the SERVER — nothing in the browser can set an httpOnly cookie's lifetime, which
   * is why this is a field on the request rather than something this layer does. `0028` §5.
   */
  readonly signIn: (
    email: string,
    password: string,
    remember?: boolean,
    /**
     * ⚠️ **The second factor, when the server has asked for one — item 11.** Absent on the first
     * attempt: the screen cannot know an account has a factor without trying, and a way to ask
     * beforehand would be an oracle for which addresses have accounts.
     */
    second?: { readonly totp?: string; readonly recoveryKey?: string },
  ) => Promise<{ ok: boolean; code?: string }>;
  /**
   * ⚠️ **No email argument, and that is the security property.** The ceremony asks the
   * authenticator what it holds for this site, so the page never says who is signing in — which
   * is what stops the sign-in screen becoming an oracle for whether an address has a passkey.
   */
  readonly signInWithPasskey: (remember?: boolean) => Promise<{ ok: boolean; code?: string }>;
  /**
   * ⚠️ **The code and the verifier, exchanged BY THE API — item 2.** Nothing here parses a token:
   * `pkce.ts` carries the argument for why the browser is never allowed to hold what GoTrue would
   * otherwise hand it in a URL fragment.
   */
  readonly signInWithOAuth: (
    code: string,
    codeVerifier: string,
    remember?: boolean,
  ) => Promise<{ ok: boolean; code?: string }>;
  readonly signOut: () => Promise<void>;
  /**
   * Re-read `/auth/me`.
   *
   * ⚠️⚠️ **C17'S INVITATION ACCEPTANCE NEEDS THIS, and nothing else does.** The workspace list is
   * fetched once, when the session is established — but accepting an invitation CREATES a membership
   * after that, so the app is holding a list that does not contain the workspace somebody just
   * joined. Navigating there would land them on "no workspace", which reads as the join having
   * failed.
   */
  readonly reload: () => Promise<void>;
  readonly selectWorkspace: (id: string) => void;
  /**
   * The authenticated `fetch` for everything else.
   *
   * ⚠️ **It refreshes once on a 401 and retries, then gives up.** Without the retry, an access token
   * that expired thirty seconds ago turns every screen into a sign-in prompt. With an unbounded
   * retry, a genuinely revoked session becomes an infinite loop against the API — which is how a
   * revoked laptop DDoSes its own server.
   */
  readonly request: (path: string, init?: RequestInit) => Promise<Response>;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

/**
 * ⚠️⚠️ **EVERY FIELD IS OPTIONAL, AND THAT IS THE POINT RATHER THAN SLOPPINESS.** This describes
 * *what arrived over the wire*, not what the endpoint promises — and `load` below treats a body
 * that does not match as "no session" instead of reading through it. Declaring the fields as
 * required would make the narrowing below look redundant to the compiler and to the linter, which
 * is how the checks get deleted and how a wrong-shaped 200 becomes a permanent spinner.
 */
interface MeResponse {
  user?: {
    id?: string;
    email?: string;
    display_name?: string | null;
    given_name?: string | null;
    avatar_url?: string | null;
  };
  workspaces?: {
    id?: string;
    slug?: string;
    name?: string;
    has_icon?: boolean;
    member_id?: string;
    tenant_status?: string;
  }[];
  setup?: { finished?: boolean; organisation_name?: string | null; profile_done?: boolean };
}

/** C42 · one key, named for what it holds rather than for the feature that writes it. */
const REMEMBERED_WORKSPACE = 'forecastify.workspace';

/**
 * 21 Oct 2026 · **a worklog was just saved** — what `tracker-connection.tsx` listens for, so the
 * *"your hours are not reaching Jira"* prompt is raised by every save point without each one
 * remembering to. ⚠️ Told AFTER a successful `POST /worklogs`, with a copy of the body; the screen's
 * own read of the response is untouched.
 */
type WorklogListener = (worklog: { readonly syncState: string | null }) => void;
const worklogListeners = new Set<WorklogListener>();

/** Subscribes; returns the unsubscribe, so it fits a `useEffect`. */
export function onWorklogCreated(listener: WorklogListener): () => void {
  worklogListeners.add(listener);
  return () => {
    worklogListeners.delete(listener);
  };
}

/** Exported for its test; `request` below is the only caller. */
export function announceWorklog(
  path: string,
  init: RequestInit | undefined,
  response: Response,
): void {
  if (worklogListeners.size === 0 || !response.ok) return;
  if ((init?.method ?? 'GET').toUpperCase() !== 'POST' || path !== '/worklogs') return;
  void response
    .clone()
    .json()
    .then((body: unknown) => {
      const syncState =
        typeof body === 'object' && body !== null && 'syncState' in body ? body.syncState : null;
      const worklog = { syncState: typeof syncState === 'string' ? syncState : null };
      for (const listener of worklogListeners) listener(worklog);
    })
    .catch(() => undefined);
}

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('restoring');
  const [email, setEmail] = useState<string | null>(null);
  /* C22 · what the sidebar chip draws. Both may be absent — an older API, or a person with no
   * picture — and the shell falls back to the address either way. */
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [givenName, setGivenName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [setup, setSetup] = useState<SessionState['setup']>({
    finished: true,
    organisationName: null,
    profileDone: true,
  });
  const [workspaces, setWorkspaces] = useState<readonly Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  /**
   * C42 · ⚠️⚠️ **THE CHOSEN WORKSPACE HAS TO SURVIVE A PAGE LOAD, AND THE FIRST VERSION DID NOT.**
   * The token lives in memory by design (see the header), so every reload re-reads `/auth/me` and
   * `?? list[0]` picked the first workspace again — somebody switched, hit refresh, and was
   * silently back where they started, with the sidebar showing one workspace's projects under
   * another's name for as long as it took them to notice.
   *
   * ⚠️ `localStorage` and not a cookie: it is a per-browser preference, never sent anywhere, and
   * every request already names the workspace explicitly. Wrapped, because a private window and a
   * browser set to block site data both throw on access rather than returning null.
   */
  const remember = (id: string | null): void => {
    try {
      if (id === null) localStorage.removeItem(REMEMBERED_WORKSPACE);
      else localStorage.setItem(REMEMBERED_WORKSPACE, id);
    } catch {
      /* A browser that will not store it is a browser that gets the default. Not an error. */
    }
  };

  /**
   * ⚠️ **A ref, not state, and that is not a micro-optimisation.** `request` is handed to screens and
   * called from effects; if the token lived in state, every consumer would capture a stale closure
   * over the value at render time — and the symptom is one 401 immediately after a refresh, on
   * whichever screen happened to render first. A ref is read at call time.
   */
  const token = useRef<string | null>(null);

  /** Who this token belongs to, so a stale response cannot overwrite a newer session. */
  const generation = useRef(0);

  const load = useCallback(async (accessToken: string): Promise<boolean> => {
    const response = await fetch(apiUrl('/auth/me'), {
      headers: { authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    }).catch(() => null);
    if (response?.ok !== true) return false;

    /**
     * ⚠️⚠️ **`.catch`, AND WITHOUT IT A 200 THAT IS NOT JSON HANGS THE WHOLE APP.** `refresh` below
     * documents at length why a network failure is caught rather than left to reject — an uncaught
     * rejection inside the restore effect leaves `status` at `restoring` **forever**. A body that
     * parses badly does exactly the same thing through a different door, and that door is easier to
     * walk through than it sounds: anything answering on the API's port that is not the API — a
     * proxy error page, a dev server from another project, a captive portal — returns a perfectly
     * successful response whose body is HTML. Found for real: an unrelated Nuxt server holding
     * `:3000` sat the entry suite on "Munkamenet ellenőrzése…" until it timed out.
     */
    const body = (await response.json().catch(() => null)) as MeResponse | null;

    /**
     * ⚠️ **Both halves checked, because both halves are read.** A body with a `user` and no
     * `workspaces` array would pass a `user`-only guard and then throw on `.map` — same unhandled
     * rejection, same permanent "Munkamenet ellenőrzése…", one field further along.
     */
    if (body === null || typeof body.user?.email !== 'string' || !Array.isArray(body.workspaces)) {
      return false;
    }

    setEmail(body.user.email);
    setDisplayName(typeof body.user.display_name === 'string' ? body.user.display_name : null);
    /* ⚠️ `0148`. An older API omits it, and the narrowing turns that into `null` rather than
       `undefined` — which is the difference between a greeting without a name and `"Hi undefined"`. */
    setGivenName(typeof body.user.given_name === 'string' ? body.user.given_name : null);
    setAvatarUrl(typeof body.user.avatar_url === 'string' ? body.user.avatar_url : null);
    setSetup({
      finished: body.setup?.finished !== false,
      organisationName:
        typeof body.setup?.organisation_name === 'string' ? body.setup.organisation_name : null,
      /* ⚠️ `true` unless the server says otherwise, like `finished`, for the same reason. */
      profileDone: body.setup?.profile_done !== false,
    });
    /* ⚠️ `flatMap` rather than `map`, so a malformed ROW is dropped instead of becoming a workspace
     * whose id is `undefined` — which would be sent as the `x-workspace-id` header on every
     * subsequent request and 404 all of them. */
    const list = body.workspaces.flatMap((row) =>
      typeof row.id === 'string' &&
      typeof row.slug === 'string' &&
      typeof row.member_id === 'string'
        ? [
            {
              id: row.id,
              slug: row.slug,
              memberId: row.member_id,
              /* ⚠️ **Falls back to the slug, never to an empty string.** An older API sends no name,
                 and a switcher row labelled `""` is worse than one labelled `delivery`. */
              name: typeof row.name === 'string' && row.name !== '' ? row.name : row.slug,
              hasIcon: row.has_icon === true,
              /* ⚠️ **`suspended` only when the server says so.** Anything else — a missing field,
               * an unknown value from a newer API — is treated as working, because the failure
               * mode of guessing the other way is locking everybody out of a healthy product. */
              tenantStatus:
                row.tenant_status === 'suspended' ? ('suspended' as const) : ('active' as const),
            },
          ]
        : [],
    );
    setWorkspaces(list);
    /* ⚠️ Picking the first is a CLIENT convenience and not the server guessing — R-3's objection is
     * to the API defaulting, which would bake single-workspace into the request path. C42 built the
     * switcher on top of it; `?? current` is what stops a `/auth/me` refresh moving somebody out of
     * the workspace they just chose. */
    /**
     * ⚠️ **Only if it is still a membership.** A remembered id whose workspace somebody has since
     * been removed from would be sent as `x-workspace-id` on every request and 404 all of them —
     * which reads as the product being broken rather than as access having been withdrawn.
     */
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(REMEMBERED_WORKSPACE);
    } catch {
      stored = null;
    }
    const remembered = list.some((entry) => entry.id === stored) ? stored : null;
    setWorkspaceId((current) => current ?? remembered ?? list[0]?.id ?? null);
    return true;
  }, []);

  /**
   * Mint a new access token from the httpOnly refresh cookie.
   *
   * ⚠️⚠️ **A NETWORK FAILURE IS CAUGHT, NOT LEFT TO REJECT — and this was a real bug.** `fetch`
   * rejects (rather than resolving with a status) when the API is unreachable: wrong port, not
   * started, DNS gone, offline. An uncaught rejection inside the restore effect left `status` at
   * `restoring` **forever**, so the whole application sat on "checking your session" with no error
   * anywhere — which is precisely the "every page just shows loading" symptom the session layer was
   * built to fix, reproduced one level up.
   *
   * Treating it as signed-out is the honest answer: we cannot prove there is a session, so we must
   * not claim one. The sign-in screen then fails loudly with a message when the API is still down.
   */
  const refresh = useCallback(async (): Promise<string | null> => {
    const response = await fetch(apiUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
    }).catch(() => null);
    if (response?.ok !== true) return null;

    /* ⚠️ Same `.catch` as `load`, for the same reason and against the same 200-with-HTML: the
     * paragraph above is about `fetch` REJECTING, and this is the case where it resolves and the
     * body is the problem. Treating it as "no session" is the honest answer — we cannot prove there
     * is one. */
    const body = (await response.json().catch(() => null)) as { access_token?: unknown } | null;
    return typeof body?.access_token === 'string' ? body.access_token : null;
  }, []);

  /* On load: there is no token in memory, so the only hope is the cookie. */
  useEffect(() => {
    const mine = ++generation.current;

    void (async () => {
      const restored = await refresh();
      if (generation.current !== mine) return;

      if (restored === null) {
        setStatus('signed-out');
        return;
      }

      token.current = restored;
      const ok = await load(restored);
      if (generation.current !== mine) return;
      setStatus(ok ? 'signed-in' : 'signed-out');
    })();

    /**
     * ⚠️⚠️ **UNMOUNT RETIRES THE GENERATION, AND WITHOUT THIS LINE THE SUITE FAILS WITHOUT FAILING
     * A TEST.** The guards above are written against a *stale response overwriting a newer
     * session* — a second effect run, or a sign-out — and both of those bump the counter
     * themselves. Unmount bumped nothing, so `generation.current === mine` still held afterwards
     * and `setStatus` ran on a component that no longer exists.
     *
     * In a browser React 19 simply drops that update, which is why this survived. Under vitest it
     * does not: the jsdom environment is torn down when the file finishes, so a refresh still in
     * flight resolves into a world with no `window` and react-dom throws `ReferenceError: window is
     * not defined` from `resolveUpdatePriority`. The throw belongs to no case, so vitest reports it
     * as an unhandled rejection after the summary and exits 1 under `Tests 474 passed` — the same
     * shape as S3-3, and the totals line reads as a pass. It is a RACE: whether the promise
     * outlives the file depends on scheduling, so it appeared on CI while three consecutive local
     * runs were green, and the file it was attributed to (`people.test.tsx`) had nothing to do
     * with it.
     */
    return () => {
      generation.current += 1;
    };
  }, [load, refresh]);

  /**
   * ⚠️ **It reuses `load()`, which is the same call the sign-in path makes** — so a membership that
   * appeared mid-session is picked up by exactly the code that would have seen it at sign-in. A
   * second bespoke fetch here would be a second place for the workspace-selection rule to live.
   */
  const reload = useCallback<SessionState['reload']>(async () => {
    const current = token.current;
    if (current === null) return;
    await load(current);
  }, [load]);

  const signIn = useCallback<SessionState['signIn']>(
    async (address, password, remember = true, second) => {
      const response = await fetch(apiUrl('/auth/sign-in'), {
        method: 'POST',
        /* ⚠️ `credentials: 'include'` or the refresh cookie is DISCARDED AT ISSUE. A cross-origin
         * `Set-Cookie` is only stored if the request that produced it asked for credentials — the
         * outgoing project spent five work packages on that exact bug. */
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: address,
          password,
          remember,
          /* ⚠️ Omitted rather than sent as `undefined`: the schema's fields are `.optional()`, and
             `JSON.stringify` drops an undefined value anyway — spelling it out here is what keeps
             an empty string from ever reaching a regex that would reject it as malformed. */
          ...(second?.totp === undefined || second.totp === '' ? {} : { totp: second.totp }),
          ...(second?.recoveryKey === undefined || second.recoveryKey === ''
            ? {}
            : { recovery_key: second.recoveryKey }),
        }),
      }).catch(() => null);

      /* An unreachable API is `unavailable`, not `invalid_credentials` — telling somebody their
       * password is wrong when the server is down sends them to reset a password that works. */
      if (response === null) return { ok: false, code: 'unavailable' };

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        return { ok: false, code: body?.error?.code ?? 'unknown' };
      }

      const body = (await response.json()) as { access_token: string };
      token.current = body.access_token;

      const ok = await load(body.access_token);
      setStatus(ok ? 'signed-in' : 'signed-out');
      return ok ? { ok: true } : { ok: false, code: 'no_membership' };
    },
    [load],
  );

  const signInWithCode = useCallback<SessionState['signInWithCode']>(
    async (address, code) => {
      /* ⚠️ `credentials: 'include'`, or the refresh cookie is discarded at issue — `signIn` above. */
      const response = await fetch(apiUrl('/auth/sign-up/verify'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: address, code }),
      }).catch(() => null);
      if (response === null) return { ok: false, code: 'unavailable' };
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        return { ok: false, code: body?.error?.code ?? 'unknown' };
      }
      const body = (await response.json()) as { access_token: string };
      token.current = body.access_token;
      const ok = await load(body.access_token);
      setStatus(ok ? 'signed-in' : 'signed-out');
      return ok ? { ok: true } : { ok: false, code: 'unavailable' };
    },
    [load],
  );

  const signInWithOAuth = useCallback<SessionState['signInWithOAuth']>(
    async (code, codeVerifier, remember = true) => {
      const response = await fetch(apiUrl('/auth/oauth/callback'), {
        method: 'POST',
        /* ⚠️ `credentials: 'include'` or the refresh cookie is DISCARDED AT ISSUE — the same trap
           `signIn` above records, and the reason it is spelled out at every call site. */
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code, code_verifier: codeVerifier, remember }),
      }).catch(() => null);

      if (response === null) return { ok: false, code: 'unavailable' };

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        return { ok: false, code: body?.error?.code ?? 'unknown' };
      }

      const body = (await response.json()) as { access_token: string };
      token.current = body.access_token;

      const ok = await load(body.access_token);
      setStatus(ok ? 'signed-in' : 'signed-out');
      return ok ? { ok: true } : { ok: false, code: 'no_membership' };
    },
    [load],
  );

  const signInWithPasskey = useCallback<SessionState['signInWithPasskey']>(
    async (remember = true) => {
      const begin = await fetch(apiUrl('/auth/passkeys/sign-in/options'), {
        method: 'POST',
        credentials: 'include',
      }).catch(() => null);
      if (begin?.ok !== true) return { ok: false, code: 'unavailable' };

      /* Throws on a dismissed prompt; the caller decides whether that is worth a message. */
      const assertion = await getPasskeyAssertion(
        (await begin.json()) as Parameters<typeof getPasskeyAssertion>[0],
      );

      const response = await fetch(apiUrl('/auth/passkeys/sign-in'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ response: assertion, remember }),
      }).catch(() => null);

      if (response?.ok !== true) return { ok: false, code: 'invalid_credentials' };

      const body = (await response.json()) as { access_token: string };
      token.current = body.access_token;

      const ok = await load(body.access_token);
      setStatus(ok ? 'signed-in' : 'signed-out');
      return ok ? { ok: true } : { ok: false, code: 'no_membership' };
    },
    [load],
  );

  const signOut = useCallback(async (): Promise<void> => {
    generation.current += 1;
    /* Best effort: the local state is cleared either way, because a failed sign-out that leaves the
     * app looking signed in is worse than one that does not reach the server. */
    await fetch(apiUrl('/auth/sign-out'), { method: 'POST', credentials: 'include' }).catch(
      () => undefined,
    );
    token.current = null;
    setEmail(null);
    setSetup({ finished: true, organisationName: null, profileDone: true });
    setWorkspaces([]);
    setWorkspaceId(null);
    /* ⚠️ Forgotten on sign-out: the next person to use this browser is not necessarily the same
       person, and a remembered id they are not a member of is a 404 on every screen. */
    remember(null);
    setStatus('signed-out');
  }, []);

  const request = useCallback<SessionState['request']>(
    async (path, init) => {
      const send = (accessToken: string | null): Promise<Response> => {
        /**
         * ⚠️ **A real `Headers`, not an object spread.** `RequestInit['headers']` may be a `Headers`
         * instance or an array of pairs, and `{ ...someHeaders }` on either produces numeric indices
         * or nothing at all — so a caller passing `new Headers()` would silently lose every header
         * INCLUDING the ones added below. `Headers` merges all three shapes correctly.
         */
        const headers = new Headers(init?.headers);
        if (accessToken !== null) headers.set('authorization', `Bearer ${accessToken}`);
        /**
         * ⚠️ Every guarded route needs this, and the guard answers 404 without it — which reads as
         * "no such endpoint" rather than "you did not say which workspace".
         *
         * ⚠️⚠️ **AN EXPLICIT HEADER FROM THE CALLER WINS, AND C42's SWITCHER IS WHY.** The workspace
         * menu draws an icon for every workspace somebody belongs to, including the ones they are
         * not currently in — and `/files/workspace-icon/:id` is workspace-scoped, so a request for
         * another workspace's picture has to be *made in* that workspace. The caller is a member of
         * it, so the guard allows it; it just has to be able to say which one it means. Overwriting
         * the header here would have made that impossible and the reason invisible.
         */
        if (!headers.has('x-workspace-id') && workspaceId !== null) {
          headers.set('x-workspace-id', workspaceId);
        }
        /**
         * Only when there is a body: sending it on a GET makes some proxies expect one.
         *
         * ⚠⚠️ **AND NEVER FOR `FormData`, WHICH C22 FOUND THE HARD WAY.** A multipart body's
         * content type carries a **boundary** the browser generates — `multipart/form-data;
         * boundary=----WebKitFormBoundary…` — and it can only attach it if nothing has already set
         * the header. Overriding it to `application/json` leaves the server a multipart body
         * labelled as JSON, so the parser finds no file and answers *"No file was sent"*: a 400
         * that blames the caller for something this line did.
         */
        if (init?.body !== undefined && !(init.body instanceof FormData)) {
          headers.set('content-type', 'application/json');
        }

        return fetch(apiUrl(path), { ...init, credentials: 'include', headers });
      };

      /* ⚠️ Not caught here, deliberately: a screen's own fetch failing is a screen-level problem and
       * it should see the rejection. What must never happen is the SESSION being left in an
       * indeterminate state by one, which is why `refresh` and `load` catch and this does not. */
      const first = await send(token.current);
      if (first.status !== 401) {
        announceWorklog(path, init, first);
        return first;
      }

      /* ⚠️ ONE refresh, then one retry, then the answer stands. An unbounded loop against a revoked
       * session is a laptop attacking its own API. */
      const renewed = await refresh();
      if (renewed === null) {
        token.current = null;
        setStatus('signed-out');
        return first;
      }

      token.current = renewed;
      const second = await send(renewed);
      announceWorklog(path, init, second);
      return second;
    },
    [refresh, workspaceId],
  );

  const value = useMemo<SessionState>(
    () => ({
      status,
      email,
      workspaces,
      workspaceId,
      displayName,
      givenName,
      avatarUrl,
      setup,
      signIn,
      signInWithCode,
      signInWithPasskey,
      signInWithOAuth,
      signOut,
      selectWorkspace: (id: string) => {
        remember(id);
        setWorkspaceId(id);
      },
      reload,
      request,
    }),
    [
      status,
      email,
      displayName,
      givenName,
      avatarUrl,
      setup,
      workspaces,
      workspaceId,
      signIn,
      signInWithCode,
      signInWithPasskey,
      signInWithOAuth,
      signOut,
      reload,
      request,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  /* Throws rather than returning a signed-out stub: a component rendered outside the provider would
   * otherwise show a sign-in prompt forever and look like an auth bug. */
  if (value === undefined) throw new Error('useSession was called outside SessionProvider');
  return value;
}
